import { useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { analyticsService } from '@/services/api'
import {
  ArrowLeft, Users, CheckCircle, XCircle, Clock, Download,
  Search, Filter, Award, TrendingUp,
} from 'lucide-react'
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts'
import { format, parseISO, startOfMonth } from 'date-fns'
import PeriodFilter from '@/components/analytics/PeriodFilter'

const STATUS_BADGE = {
  on_time: 'badge-green', late: 'badge-yellow',
  very_late: 'badge-red', absent: 'badge-red', no_schedule: 'badge-gray',
}
const STATUS_LABEL = {
  on_time: 'Вовремя', late: 'Опоздал', very_late: 'Сильн. опозд.',
  absent: 'Не явился', no_schedule: 'Нет расп.',
}

function KPICard({ label, value, sub, color = 'text-gray-900', icon: Icon }) {
  return (
    <div className="card py-5">
      <div className="flex items-start gap-3">
        {Icon && <Icon size={20} className={`${color} shrink-0 mt-0.5`} />}
        <div>
          <p className={`text-3xl font-bold ${color}`}>{value ?? 0}</p>
          {sub && <p className="text-xs text-gray-400">{sub}</p>}
          <p className="text-sm text-gray-500 mt-0.5">{label}</p>
        </div>
      </div>
    </div>
  )
}

function TabBar({ tabs, active, onChange }) {
  return (
    <div className="flex border-b border-gray-200 gap-1">
      {tabs.map((t) => (
        <button key={t.key} onClick={() => onChange(t.key)}
          className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
            active === t.key
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}>
          {t.label}
        </button>
      ))}
    </div>
  )
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = filename; a.click()
  URL.revokeObjectURL(url)
}

function StudentRow({ s, navigate }) {
  return (
    <tr className="cursor-pointer hover:bg-gray-50"
      onClick={() => navigate(`/students/${s.student_id}`)}>
      <td>
        {s.photo
          ? <img src={s.photo} alt="" className="w-8 h-8 rounded-full object-cover" />
          : <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-400">
              {s.full_name?.[0]}
            </div>}
      </td>
      <td className="font-medium text-primary-600">{s.full_name}</td>
      <td>
        <span className={`badge text-xs ${STATUS_BADGE[s.today_status] || 'badge-gray'}`}>
          {STATUS_LABEL[s.today_status] || s.today_status}
        </span>
      </td>
      <td className="font-mono text-xs text-green-700">{s.check_in || '—'}</td>
      <td>
        <span className={`badge text-xs ${s.attendance_percent >= 75 ? 'badge-green' : s.attendance_percent >= 50 ? 'badge-yellow' : 'badge-red'}`}>
          {s.attendance_percent}%
        </span>
      </td>
      <td className="text-yellow-600">{s.late_count || 0}</td>
      <td>
        <button className="btn-secondary btn-sm text-xs"
          onClick={(e) => { e.stopPropagation(); navigate(`/students/${s.student_id}`) }}>
          Профиль
        </button>
      </td>
    </tr>
  )
}

const TABS = [
  { key: 'overview', label: 'Обзор' },
  { key: 'students', label: 'Студенты' },
  { key: 'stats', label: 'Статистика' },
]

export default function GroupAnalyticsPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const today = new Date()
  const [tab, setTab] = useState('overview')
  const [period, setPeriod] = useState({
    date_from: format(startOfMonth(today), 'yyyy-MM-dd'),
    date_to: format(today, 'yyyy-MM-dd'),
  })
  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['group-analytics', id, period],
    queryFn: () => analyticsService.classDetail(id, period).then((r) => r.data),
  })

  const studentParams = useMemo(() => ({
    ...period,
    ...(search && { search }),
    ...(filterStatus && { status: filterStatus }),
  }), [period, search, filterStatus])

  const { data: students = [], isFetching: studentsLoading } = useQuery({
    queryKey: ['group-students', id, studentParams],
    queryFn: () => analyticsService.classStudents(id, studentParams).then((r) => r.data),
    enabled: tab === 'students',
  })

  const handleExport = async () => {
    try {
      const res = await analyticsService.exportClassExcel(id, period)
      downloadBlob(res.data, `group_${id}_${period.date_from}_${period.date_to}.xlsx`)
    } catch { /* silent */ }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary-600 border-t-transparent" />
      </div>
    )
  }
  if (!data) return <div className="text-center py-20 text-gray-400">Данные не найдены</div>

  const chartData = (data.trend || []).map((d) => ({
    label: format(parseISO(String(d.date)), 'dd.MM'),
    total: d.total, on_time: d.on_time, late: d.late,
  }))

  // Bar data for student ranking
  const rankingBar = (data.student_ranking || []).slice(0, 10).map((s) => ({
    name: s.full_name.split(' ')[0], // first name only for brevity
    '%': s.on_time_percent,
  }))

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="btn-secondary btn-sm">
            <ArrowLeft size={16} /> Назад
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Группа {data.class?.name}</h1>
            <p className="text-sm text-gray-500">
              {} · {} · {} курс
            </p>
          </div>
        </div>
        <button onClick={handleExport} className="btn-secondary btn-sm gap-1.5">
          <Download size={15} /> Экспорт Excel
        </button>
      </div>

      <div className="card py-3">
        <PeriodFilter onChange={setPeriod} />
      </div>

      <TabBar tabs={TABS} active={tab} onChange={setTab} />

      {/* ── OVERVIEW ── */}
      {tab === 'overview' && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <KPICard label="Всего студентов" value={data.total_students} icon={Users} color="text-primary-600" />
            <KPICard label="Присутствуют" value={data.present_today} icon={CheckCircle}
              color="text-green-600" sub={`вовремя: ${data.on_time_today}`} />
            <KPICard label="Отсутствуют" value={data.absent_today} icon={XCircle}
              color="text-red-600" sub={`опоздало: ${data.late_today}`} />
            <KPICard label="% вовремя за период" value={`${data.on_time_percent ?? 0}%`}
              icon={Clock} color="text-blue-600" sub={`ср. опозд. ${data.avg_late_minutes} мин.`} />
          </div>

          {/* Today's status table */}
          {data.students_today?.length > 0 && (
            <div className="card">
              <h3 className="font-semibold text-gray-800 mb-4">Статус студентов сегодня</h3>
              <div className="table-container max-h-80 overflow-y-auto">
                <table>
                  <thead>
                    <tr><th className="w-10"></th><th>ФИО</th><th>Приход</th><th>Статус</th><th>Опоздание</th></tr>
                  </thead>
                  <tbody>
                    {data.students_today.map((s) => (
                      <tr key={s.student_id} className="cursor-pointer hover:bg-gray-50"
                        onClick={() => navigate(`/students/${s.student_id}`)}>
                        <td>
                          {s.photo
                            ? <img src={s.photo} alt="" className="w-8 h-8 rounded-full object-cover" />
                            : <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-400 font-medium">
                                {s.full_name?.[0]}
                              </div>}
                        </td>
                        <td className="font-medium text-primary-600">{s.full_name}</td>
                        <td className="font-mono text-xs text-green-700">{s.check_in || '—'}</td>
                        <td>
                          <span className={`badge text-xs ${STATUS_BADGE[s.status] || 'badge-gray'}`}>
                            {STATUS_LABEL[s.status] || s.status}
                          </span>
                        </td>
                        <td className="text-gray-500 text-sm">
                          {s.late_minutes > 0 ? `${s.late_minutes} мин.` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {chartData.length > 1 && (
            <div className="card">
              <h3 className="font-semibold text-gray-800 mb-4">Тренд посещаемости</h3>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip /><Legend />
                  <Area type="monotone" dataKey="total" name="Всего" stroke="#94a3b8" fill="#f1f5f9" strokeWidth={1} />
                  <Area type="monotone" dataKey="on_time" name="Вовремя" stroke="#22c55e" fill="#dcfce7" strokeWidth={2} />
                  <Area type="monotone" dataKey="late" name="Опоздало" stroke="#f59e0b" fill="#fef3c7" strokeWidth={1} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* ── STUDENTS ── */}
      {tab === 'students' && (
        <div className="space-y-4">
          <div className="card py-3">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-48">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input className="input pl-9 py-1.5 text-sm w-full" placeholder="Поиск по ФИО..."
                  value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <select className="input py-1.5 text-sm" value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="">Любой статус</option>
                <option value="on_time">Вовремя</option>
                <option value="late">Опоздал</option>
                <option value="very_late">Сильн. опозд.</option>
                <option value="absent">Отсутствует</option>
              </select>
              {(search || filterStatus) && (
                <button className="btn-secondary btn-sm"
                  onClick={() => { setSearch(''); setFilterStatus('') }}>
                  <Filter size={14} /> Сбросить
                </button>
              )}
            </div>
          </div>

          <div className="card">
            <h3 className="font-semibold text-gray-800 mb-3">
              Студенты группы
              <span className="text-gray-400 font-normal ml-2">({students.length})</span>
            </h3>
            {studentsLoading ? (
              <div className="flex justify-center py-10">
                <div className="animate-spin h-8 w-8 border-2 border-primary-600 border-t-transparent rounded-full" />
              </div>
            ) : students.length === 0 ? (
              <div className="text-center py-10 text-gray-400">Нет студентов по заданным фильтрам</div>
            ) : (
              <div className="table-container max-h-[520px] overflow-y-auto">
                <table>
                  <thead>
                    <tr>
                      <th className="w-10"></th><th>ФИО</th><th>Статус сегодня</th>
                      <th>Приход</th><th>% посещ.</th><th>Опозданий</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((s) => <StudentRow key={s.student_id} s={s} navigate={navigate} />)}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── STATISTICS ── */}
      {tab === 'stats' && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <KPICard label="Всего студентов" value={data.total_students} icon={Users} color="text-primary-600" />
            <KPICard label="% вовремя" value={`${data.on_time_percent ?? 0}%`}
              icon={CheckCircle} color="text-green-600" />
            <KPICard label="% опозданий" value={`${data.late_percent ?? 0}%`}
              icon={TrendingUp} color="text-yellow-600" />
            <KPICard label="Ср. опоздание" value={`${data.avg_late_minutes ?? 0} мин.`}
              icon={Clock} color="text-orange-600" />
          </div>

          {/* Student ranking bar */}
          {rankingBar.length > 0 && (
            <div className="card">
              <h3 className="font-semibold text-gray-800 mb-4">ТОП-10 студентов по посещаемости</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={rankingBar} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={70} />
                  <Tooltip formatter={(v) => `${v}%`} />
                  <Bar dataKey="%" fill="#22c55e" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Full ranking table */}
          {data.student_ranking?.length > 0 && (
            <div className="card">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Award size={18} className="text-yellow-500" /> Рейтинг студентов за период
              </h3>
              <div className="table-container max-h-96 overflow-y-auto">
                <table>
                  <thead>
                    <tr>
                      <th>#</th><th>ФИО</th><th>Явок</th><th>Вовремя</th>
                      <th>Опоздания</th><th>% вовремя</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.student_ranking.map((s, i) => (
                      <tr key={s.student_id} className="cursor-pointer hover:bg-gray-50"
                        onClick={() => navigate(`/students/${s.student_id}`)}>
                        <td className={`font-bold ${i < 3 ? 'text-yellow-500' : 'text-gray-400'}`}>{i + 1}</td>
                        <td className="font-medium text-primary-600">{s.full_name}</td>
                        <td>{s.total}</td>
                        <td className="text-green-600">{s.on_time}</td>
                        <td className="text-yellow-600">{s.late}</td>
                        <td>
                          <span className={`badge text-xs ${s.on_time_percent >= 75 ? 'badge-green' : s.on_time_percent >= 50 ? 'badge-yellow' : 'badge-red'}`}>
                            {s.on_time_percent}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {chartData.length > 1 && (
            <div className="card">
              <h3 className="font-semibold text-gray-800 mb-4">Динамика посещаемости</h3>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip /><Legend />
                  <Area type="monotone" dataKey="on_time" name="Вовремя" stroke="#22c55e" fill="#dcfce7" strokeWidth={2} />
                  <Area type="monotone" dataKey="late" name="Опоздало" stroke="#f59e0b" fill="#fef3c7" strokeWidth={1} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
